import type { FC } from "react";

import type { GroupedPurposesBySection } from "@ui/components/ConsentSection";
import { ConsentSection } from "@ui/components/ConsentSection";
import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useTranslations } from "@ui/providers/useTranslations";

import { Button } from "@jti/core-components/components/Button";
import { Card } from "@jti/core-components/components/Card";
import type { AcceptAllButtonMode, OptionalConsentsLayout, OtPurpose } from "@jti/fe-common";

import { SingleOptionalConsent } from "./components";
import { OptionalConsentRadioGroup } from "./OptionalConsentRadioGroup";

import styles from "./OptionalConsents.module.scss";

type OptionalConsentsProps = {
  purposes?: OtPurpose[] | GroupedPurposesBySection[];
  disabled?: boolean;
  layout?: OptionalConsentsLayout;
  acceptAllButtonMode?: AcceptAllButtonMode;
  singleOptionConsentsDefaultValue?: "true" | "false";
  onAcceptAllClick?: () => void;
  onDeclineAllClick?: () => void;
  required?: boolean;
  isColumnDisplayOfConsents?: boolean;
};

export const OptionalConsents: FC<OptionalConsentsProps> = ({
  purposes = [],
  disabled,
  layout = "default",
  acceptAllButtonMode = "none",
  singleOptionConsentsDefaultValue,
  onAcceptAllClick,
  onDeclineAllClick,
  required = true,
  isColumnDisplayOfConsents = false,
}) => {
  const { breakpoint } = useDeviceBreakpoint();
  const { __ } = useTranslations();
  const buttonSize = breakpoint === "mobile" ? "sm" : "md";

  if (!purposes.length) {
    return null;
  }

  return (
    <Card data-testid="optionalConsentsWrapper">
      <div className={styles.content}>
        {["extended", "section"].includes(layout) && (
          <div className={styles.consentSectionsContainer}>
            {(purposes as GroupedPurposesBySection[]).map((section) => (
              <ConsentSection
                key={section.purposeId}
                section={section}
                disabled={disabled}
                required={required}
                isColumnDisplayOfConsents={isColumnDisplayOfConsents}
              />
            ))}
          </div>
        )}
        {layout === "default" && (
          <>
            <div className={styles.headline}>{__("auth.registration.consents.optional.description")}</div>
            {(purposes as OtPurpose[]).map(({ id, description, default: defaultValue }) => (
              <OptionalConsentRadioGroup
                key={id}
                purposeName={id}
                purposeDescription={description}
                defaultValue={defaultValue}
                disabled={disabled}
                required={required}
                isColumnDisplayOfConsents={isColumnDisplayOfConsents}
              />
            ))}
          </>
        )}
        {layout === "single_option" && (
          <SingleOptionalConsent
            disabled={disabled}
            required={required}
            defaultValue={singleOptionConsentsDefaultValue}
            purposes={purposes as GroupedPurposesBySection[]}
            onAcceptAllClick={onAcceptAllClick}
            onDeclineAllClick={onDeclineAllClick}
          />
        )}
        {acceptAllButtonMode === "optional" && (
          <div className={styles.actions}>
            <Button
              className={styles.acceptAllButton}
              data-testid="acceptAllButton"
              variant="secondary"
              size={buttonSize}
              onClick={onAcceptAllClick}
            >
              {__("auth.registration.consents.accept_all")}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};
